

































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { BeschreibungFile, BeschreibungRow, optionNumber, rowMatches } from "@/utils/BeschreibungFile";
import { COLOR_ALL_DEVICES_EDIT_ICON, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";

/** One table line: the row, plus its cells by column for sorting. */
interface SectionItem {
  lineIndex: number;
  key: string;
  category: string;
  group: string;
  entry: string;
  trade: string;
  fieldType: string;
  values: string;
  row: BeschreibungRow;
}

/**
 * A Beschreibung's sections, one table each, in the standard columns every data row follows (key,
 * category, group, entry, trade, type, values), options shown with the number references use. File
 * order by default; a heading sorts the table for reading. With `search`, only matching rows (and
 * sections with one) show, opened. Editable: a row click emits `edit-row`, its icons `insert-row` /
 * `remove-row`.
 */
@Component
export default class BeschreibungSectionsComponent extends Vue {
  @Prop({ required: true })
  file!: BeschreibungFile;

  @Prop({ default: "" })
  search!: string;

  @Prop({ type: Boolean, default: false })
  editable!: boolean;

  openPanels: number[] = [];

  /** Office's table header tint (BillingCatalogList), handed to the scoped style below. */
  /** The number an option is shown by, see `optionNumber`. */
  numberOfOption(index: number): number {
    return optionNumber(index);
  }

  get headerColor(): string {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /** The colour of Office's row icons (DeviceCategoryItem's pencil and bin). */
  get editIconColor(): string {
    return COLOR_ALL_DEVICES_EDIT_ICON;
  }

  get headers() {
    // text-no-wrap: in a narrow column (Typ) the sort arrow would wrap below the heading and lift it
    // above the others.
    const header = (key: string, value: string, extra: Record<string, unknown> = {}) => ({
      text: this.$t(`admin.infoConfig.sections.columns.${key}`).toString(),
      value,
      class: "text-no-wrap",
      ...extra,
    });
    return [
      header("key", "key", { width: "120px" }),
      header("category", "category"),
      header("group", "group"),
      header("entry", "entry"),
      header("trade", "trade"),
      header("type", "fieldType", { width: "60px" }),
      header("values", "values", { sortable: false }),
      ...(this.editable ? [{ text: "", value: "actions", sortable: false, width: "80px" }] : []),
    ];
  }

  get visibleSections() {
    return this.file.sections
      .map((section) => ({
        ...section,
        items: section.rows.filter((row) => rowMatches(row, this.search)).map((row) => this.itemOf(row)),
      }))
      .filter((section) => section.items.length > 0 || this.search.trim() === "");
  }

  /** A search opens every section with a match; clearing it closes them again. */
  @Watch("search")
  onSearchChanged(search: string) {
    this.openPanels = search.trim() === "" ? [] : this.visibleSections.map((_, index) => index);
  }

  itemOf(row: BeschreibungRow): SectionItem {
    const [category, group, entry, trade] = row.labels;
    return { lineIndex: row.lineIndex, key: row.key, category, group, entry, trade, fieldType: row.fieldType, values: row.values.join(" "), row };
  }

  /** Text order, German collation; an unsorted table keeps the file's order. */
  sortItems(items: SectionItem[], sortBy: string[], sortDesc: boolean[]): SectionItem[] {
    const column = sortBy[0] as keyof SectionItem | undefined;
    if (!column) {
      return items;
    }
    const direction = sortDesc[0] ? -1 : 1;
    return [...items].sort((a, b) => direction * String(a[column]).localeCompare(String(b[column]), "de", { numeric: true }));
  }

  onRowClicked(item: SectionItem) {
    if (this.editable) {
      this.$emit("edit-row", item.row);
    }
  }
}
